import axios, { endPoints } from 'utils/api';

// Slice mirrors the quizzes pattern, adapted to the parent/version model
// of Health Journeys: a list of journeys (latest version per row), a
// detail map keyed by publicId (holds the version array + the currently
// loaded version body), and a checklist cache keyed by `${publicId}:${v}`.

const SET_LIST = 'healthJourneys/SET_LIST';
const SET_LIST_META = 'healthJourneys/SET_LIST_META';
const SET_LIST_LOADING = 'healthJourneys/SET_LIST_LOADING';
const SET_LIST_ERROR = 'healthJourneys/SET_LIST_ERROR';
const SET_LIST_FILTERS = 'healthJourneys/SET_LIST_FILTERS';
const SET_DETAIL = 'healthJourneys/SET_DETAIL';
const SET_VERSION = 'healthJourneys/SET_VERSION';
const SET_VERSIONS = 'healthJourneys/SET_VERSIONS';
const SET_CURRENT_VERSION = 'healthJourneys/SET_CURRENT_VERSION';
const SET_CHECKLIST = 'healthJourneys/SET_CHECKLIST';
const SET_CHECKLIST_LOADING = 'healthJourneys/SET_CHECKLIST_LOADING';
const REMOVE_FROM_LIST = 'healthJourneys/REMOVE_FROM_LIST';

export const setJourneysList = (data) => ({ type: SET_LIST, data });
export const setJourneysMeta = (meta) => ({ type: SET_LIST_META, meta });
export const setJourneysLoading = (loading) => ({ type: SET_LIST_LOADING, loading });
export const setJourneysError = (error) => ({ type: SET_LIST_ERROR, error });
export const setJourneysFilters = (filters) => ({ type: SET_LIST_FILTERS, filters });
export const setJourneyDetail = (publicId, data) => ({ type: SET_DETAIL, publicId, data });
export const setJourneyVersion = (publicId, version, data) => ({
    type: SET_VERSION, publicId, version, data
});
export const setJourneyVersions = (publicId, versions) => ({
    type: SET_VERSIONS, publicId, versions
});
export const setJourneyCurrentVersion = (publicId, version) => ({
    type: SET_CURRENT_VERSION, publicId, version
});
export const setJourneyChecklist = (publicId, version, failures) => ({
    type: SET_CHECKLIST, publicId, version, failures
});
export const setJourneyChecklistLoading = (publicId, version, loading) => ({
    type: SET_CHECKLIST_LOADING, publicId, version, loading
});
export const removeJourneyFromList = (publicId) => ({ type: REMOVE_FROM_LIST, publicId });

const INITIAL_STATE = {
    list: {
        data: [],
        loading: false,
        error: null,
        meta: { total: 0, currentPage: 1, perPage: 12, lastPage: 1 },
        filters: { status: 'all', search: '', page: 1 }
    },
    detail: {
        // [publicId]: { data, versions: [], currentVersion, versionsById: { [v]: bodyObject } }
    },
    checklist: {
        // [`${publicId}:${version}`]: { failures: [], loading: false }
    }
};

function checklistKey(publicId, version) {
    return `${publicId}:${version}`;
}

// eslint-disable-next-line import/no-anonymous-default-export
export default function(state = INITIAL_STATE, action) {
    switch (action.type) {
        case SET_LIST:
            return {
                ...state,
                list: { ...state.list, data: [...action.data], error: null }
            };

        case SET_LIST_META:
            return {
                ...state,
                list: { ...state.list, meta: { ...state.list.meta, ...action.meta } }
            };

        case SET_LIST_LOADING:
            return {
                ...state,
                list: { ...state.list, loading: action.loading }
            };

        case SET_LIST_ERROR:
            return {
                ...state,
                list: { ...state.list, error: action.error, loading: false }
            };

        case SET_LIST_FILTERS:
            return {
                ...state,
                list: { ...state.list, filters: { ...state.list.filters, ...action.filters } }
            };

        case SET_DETAIL: {
            const existing = state.detail[action.publicId] || {};

            return {
                ...state,
                detail: {
                    ...state.detail,
                    [action.publicId]: {
                        ...existing,
                        data: action.data,
                        versions: existing.versions || [],
                        versionsById: existing.versionsById || {},
                        currentVersion: existing.currentVersion ??
                            action.data?.currentVersion ??
                            action.data?.version ??
                            null
                    }
                }
            };
        }

        case SET_VERSIONS: {
            const existing = state.detail[action.publicId] || { versionsById: {} };

            return {
                ...state,
                detail: {
                    ...state.detail,
                    [action.publicId]: {
                        ...existing,
                        versions: [...action.versions],
                        versionsById: existing.versionsById || {}
                    }
                }
            };
        }

        case SET_VERSION: {
            const existing = state.detail[action.publicId] || { versions: [], versionsById: {} };

            return {
                ...state,
                detail: {
                    ...state.detail,
                    [action.publicId]: {
                        ...existing,
                        data: action.data,
                        versionsById: {
                            ...(existing.versionsById || {}),
                            [action.version]: action.data
                        },
                        currentVersion: existing.currentVersion ?? action.version
                    }
                }
            };
        }

        case SET_CURRENT_VERSION: {
            const existing = state.detail[action.publicId] || { versions: [], versionsById: {} };

            return {
                ...state,
                detail: {
                    ...state.detail,
                    [action.publicId]: {
                        ...existing,
                        currentVersion: action.version
                    }
                }
            };
        }

        case SET_CHECKLIST: {
            const key = checklistKey(action.publicId, action.version);

            return {
                ...state,
                checklist: {
                    ...state.checklist,
                    [key]: { failures: action.failures || [], loading: false }
                }
            };
        }

        case SET_CHECKLIST_LOADING: {
            const key = checklistKey(action.publicId, action.version);
            const existing = state.checklist[key] || { failures: [] };

            return {
                ...state,
                checklist: {
                    ...state.checklist,
                    [key]: { ...existing, loading: action.loading }
                }
            };
        }

        case REMOVE_FROM_LIST:
            return {
                ...state,
                list: {
                    ...state.list,
                    data: state.list.data.filter((j) => j.publicId !== action.publicId)
                }
            };

        default:
            return state;
    }
}

// ── List ─────────────────────────────────────────────────────────────────

export const fetchJourneys = (params = {}) => async (dispatch) => {
    dispatch(setJourneysLoading(true));

    try {
        const query = {};

        if (params.status && params.status !== 'all') query.status = params.status;
        if (params.search) query.search = params.search;
        if (params.page) query.page = params.page;
        if (params.pageSize) query.pageSize = params.pageSize;

        const response = await axios.get(endPoints.healthJourneys, { params: query });
        const rows = response.data?.data ?? response.data ?? [];

        dispatch(setJourneysList(Array.isArray(rows) ? rows : []));

        if (response.data?.meta) {
            dispatch(setJourneysMeta(response.data.meta));
        } else if (response.data?.current_page) {
            dispatch(setJourneysMeta({
                total: response.data.total,
                currentPage: response.data.current_page,
                perPage: response.data.per_page,
                lastPage: response.data.last_page
            }));
        }
    } catch (error) {
        dispatch(setJourneysError(
            error.response?.data?.message ||
                error.message ||
                'Failed to load journeys'
        ));
    } finally {
        dispatch(setJourneysLoading(false));
    }
};

// ── Detail / versions ────────────────────────────────────────────────────

export const fetchJourneyVersions = (publicId) => async (dispatch) => {
    const response = await axios.get(`${endPoints.healthJourneys}/${publicId}/versions`);
    const rows = response.data?.data ?? response.data ?? [];

    dispatch(setJourneyVersions(publicId, Array.isArray(rows) ? rows : []));

    return rows;
};

export const fetchJourneyVersion = (publicId, version) => async (dispatch) => {
    const response = await axios.get(
        `${endPoints.healthJourneys}/${publicId}/versions/${version}`
    );

    dispatch(setJourneyVersion(publicId, version, response.data));
    dispatch(setJourneyDetail(publicId, response.data));
    dispatch(setJourneyCurrentVersion(publicId, version));

    return response.data;
};

// Convenience: load the journey landing payload — versions + the latest
// version body. Editor calls this on mount when no specific version is
// in the URL.
export const fetchJourney = (publicId) => async (dispatch) => {
    const versions = await dispatch(fetchJourneyVersions(publicId));

    if (!versions || versions.length === 0) {
        return { versions: [], data: null };
    }

    const latest = versions.reduce((a, b) => (
        (Number(b.version) || 0) > (Number(a.version) || 0) ? b : a
    ), versions[0]);

    const data = await dispatch(fetchJourneyVersion(publicId, latest.version));

    return { versions, data };
};

export const createJourney = (payload) => async (dispatch) => {
    const response = await axios.post(endPoints.healthJourneys, payload);
    // API returns the journey's publicId under `id` (no `publicId` field).
    const publicId = response.data?.publicId || response.data?.id;

    if (publicId) {
        dispatch(setJourneyDetail(publicId, response.data));

        if (response.data.version) {
            dispatch(setJourneyVersion(
                publicId,
                response.data.version,
                response.data
            ));
            dispatch(setJourneyCurrentVersion(publicId, response.data.version));
        }
    }

    return response.data;
};

export const updateJourneyVersion = (publicId, version, patch) => async (dispatch) => {
    const response = await axios.put(
        `${endPoints.healthJourneys}/${publicId}/versions/${version}`,
        patch
    );

    dispatch(setJourneyVersion(publicId, version, response.data));
    dispatch(setJourneyDetail(publicId, response.data));

    return response.data;
};

export const transitionStatus = (publicId, version, status) => async (dispatch) => {
    const response = await axios.patch(
        `${endPoints.healthJourneys}/${publicId}/versions/${version}/status`,
        { status }
    );

    dispatch(setJourneyVersion(publicId, version, response.data));
    dispatch(setJourneyDetail(publicId, response.data));

    return response.data;
};

export const fetchChecklist = (publicId, version) => async (dispatch) => {
    dispatch(setJourneyChecklistLoading(publicId, version, true));

    try {
        const response = await axios.get(
            `${endPoints.healthJourneys}/${publicId}/versions/${version}/checklist`
        );
        const failures = response.data?.failures || [];

        dispatch(setJourneyChecklist(publicId, version, failures));

        return failures;
    } catch (error) {
        if (error.response?.status === 422) {
            const failures = error.response.data?.failures ||
                error.response.data?.errors?.failures ||
                [];

            dispatch(setJourneyChecklist(publicId, version, failures));

            return failures;
        }

        dispatch(setJourneyChecklistLoading(publicId, version, false));
        throw error;
    }
};

export const duplicateJourney = (publicId, version) => async () => {
    const response = await axios.post(
        `${endPoints.healthJourneys}/${publicId}/versions/${version}/duplicate`,
        {}
    );

    return response.data;
};

export const createNewVersion = (publicId) => async (dispatch) => {
    const response = await axios.post(
        `${endPoints.healthJourneys}/${publicId}/versions`,
        {}
    );

    dispatch(setJourneyVersion(publicId, response.data.version, response.data));
    dispatch(setJourneyDetail(publicId, response.data));
    dispatch(setJourneyCurrentVersion(publicId, response.data.version));
    await dispatch(fetchJourneyVersions(publicId));

    return response.data;
};

// ── M5 Weeks ─────────────────────────────────────────────────────────────

export const createWeek = (publicId, version, payload = {}) => async (dispatch) => {
    const response = await axios.post(
        `${endPoints.healthJourneys}/${publicId}/versions/${version}/weeks`,
        payload
    );

    await dispatch(fetchJourneyVersion(publicId, version));

    return response.data;
};

export const updateWeek = (publicId, version, weekNumber, patch) => async (dispatch) => {
    const response = await axios.put(
        `${endPoints.healthJourneys}/${publicId}/versions/${version}/weeks/${weekNumber}`,
        patch
    );

    await dispatch(fetchJourneyVersion(publicId, version));

    return response.data;
};

export const deleteWeek = (publicId, version, weekNumber) => async (dispatch) => {
    await axios.delete(
        `${endPoints.healthJourneys}/${publicId}/versions/${version}/weeks/${weekNumber}`
    );

    await dispatch(fetchJourneyVersion(publicId, version));
};

// ── M2 Vouchers (Access tab) ─────────────────────────────────────────────

export const fetchJourneyVouchers = (publicId) => async () => {
    const response = await axios.get(
        `${endPoints.healthJourneys}/${publicId}/vouchers`
    );

    return response.data?.data ?? response.data ?? [];
};

export const linkJourneyVoucher = (publicId, voucherId, notifyUsers = false) => async () => {
    const response = await axios.post(
        `${endPoints.healthJourneys}/${publicId}/vouchers`,
        { voucherId, notifyUsers }
    );

    return response.data;
};

export const unlinkJourneyVoucher = (publicId, voucherId) => async () => {
    await axios.delete(
        `${endPoints.healthJourneys}/${publicId}/vouchers/${voucherId}`
    );
};

export const searchVouchers = (query) => async () => {
    const response = await axios.get('admin/vouchers', {
        params: { search: query || '', pageSize: 25 }
    });

    return response.data?.data ?? response.data ?? [];
};

export const createVoucher = (payload) => async () => {
    const response = await axios.post('admin/vouchers', payload);

    return response.data?.data ?? response.data;
};

export const searchCompanies = (query) => async () => {
    const response = await axios.get('admin/companies', {
        params: { search: query || '' }
    });

    return response.data?.data ?? response.data ?? [];
};

// ── BR-08 Audit trail ────────────────────────────────────────────────────

export const fetchAuditTrail = (publicId, params = {}) => async () => {
    const response = await axios.get(
        `${endPoints.healthJourneys}/${publicId}/audit-trail`,
        { params }
    );

    return response.data;
};
